import { stoneMedia } from '@/lib/collection-media'
import styles from './StoneThumbnail.module.css'

/**
 * The rough-stone photograph set beside the collection card's title: a circular
 * hairline frame with the cut-out inside on a transparent field, and the stone's
 * name in small gold capitals beneath. Decorative — the title already names the
 * stone — so the whole thing is aria-hidden.
 *
 * The caption names the stone actually pictured. That is a deliberate departure
 * from the family draft, whose caption rule (`gem.split('/')[0]`) printed the
 * bottle gem — "Pearl" under June's moonstone and "Topaz" under November's
 * citrine. The card title ("Pearl / Moonstone — June") and the month index still
 * lead with the bottle gem, so nothing is lost and no photograph is mislabelled.
 *
 * Every month/line change is meant to re-run the entrance (the 4s gentle spin
 * and the 400ms crossfade). The caller does that with a React `key`, which
 * remounts this element and restarts its CSS animations; no reflow is forced.
 * Under prefers-reduced-motion the module keeps only the crossfade.
 */
export default function StoneThumbnail({ name, image }: { name: string; image: string }) {
  const media = stoneMedia(image)
  return (
    <span className={styles.thumbnail} aria-hidden="true" data-stone={image}>
      <span className={styles.frame}>
        <picture className={styles.picture}>
          <source srcSet={media.avif} type="image/avif" />
          <source srcSet={media.webp} type="image/webp" />
          <img className={styles.stone} src={media.src} alt="" width={media.width} height={media.height} decoding="async" />
        </picture>
      </span>
      <span className={styles.label}>{name}</span>
    </span>
  )
}
